import { ChevronDown, ChevronRight } from 'lucide-react'
import { useCallback, useState } from 'react'

import { Panel } from '@/components/px/Panel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { FLOW_CHROME } from '@/demo/content/architecture'
import {
  activationFor,
  activationWidth,
  type FlowActivation,
  type FlowEdge,
  type FlowLane,
  type FlowStep,
} from '@/demo/customerFlow'
import { cn } from '@/lib/utils'

/**
 * The ten-step customer flow, rendered as a READABLE ARCHITECTURE ARTEFACT —
 * never as the screens that perform it (the onboarding surfaces are a separate
 * future ticket).
 *
 * ⚠ INERT (I-22). This component dispatches NOTHING and mutates NOTHING: it
 * imports no store hook at all, and its one interaction — the withheld-detail
 * disclosure — is local `useState`. Grep this file for `dispatch` and the
 * absence is the proof.
 *
 * ⚠ It renders NO STRING IT WAS NOT GIVEN (AC-6.12). Every sentence, actor,
 * gate line and object name arrives in props from `@/demo/customerFlow`, which
 * is a verbatim transcription of `px-onboarding-flow.html`; the only strings
 * this file owns are the fixed chrome labels, and those live in
 * `@/demo/content/architecture`.
 */

export interface CustomerFlowSectionProps {
  steps: readonly FlowStep[]
  activation: readonly FlowActivation[]
  edges: readonly FlowEdge[]
  lanes: readonly FlowLane[]
}

export function CustomerFlowSection({ steps, edges, lanes }: CustomerFlowSectionProps) {
  const [open, setOpen] = useState<string | null>(null)
  const onToggleDetail = useCallback(
    (n: string) => setOpen((current) => (current === n ? null : n)),
    []
  )

  const onboarding = steps.filter((s) => s.phase === 'onboarding')
  const delivery = steps.filter((s) => s.phase === 'delivery')

  return (
    <div data-testid="customer-flow">
      <div className="rounded-lg border border-border bg-card px-4 py-2">
        <table className="w-full text-left">
          <caption className="sr-only">{FLOW_CHROME.tableCaption}</caption>
          <thead>
            <tr>
              {FLOW_CHROME.columns.map((column) => (
                <th
                  key={column}
                  scope="col"
                  className="pb-2 pr-3 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <GroupRow label={`${FLOW_CHROME.onboardingGroup} · steps ${range(onboarding)}`} />
            {onboarding.map((step) => (
              <StepRow
                key={step.n}
                step={step}
                edges={edges}
                open={open === step.n}
                onToggle={onToggleDetail}
              />
            ))}
            <GroupRow label={`${FLOW_CHROME.deliveryGroup} · steps ${range(delivery)}`} />
            {delivery.map((step) => (
              <StepRow
                key={step.n}
                step={step}
                edges={edges}
                open={open === step.n}
                onToggle={onToggleDetail}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 grid grid-cols-3 items-start gap-4">
        <Panel heading={FLOW_CHROME.gatePanel.heading}>
          <p className="text-sm text-ink-soft">{FLOW_CHROME.gatePanel.body}</p>
          <p className="mt-2 text-xs text-muted-foreground">{gateWatch(steps)}</p>
        </Panel>

        <Panel heading={FLOW_CHROME.edgePanel.heading}>
          <div className="flex flex-col gap-1.5">
            {edges.map((edge) => (
              <div key={edge.n} className="flex items-center gap-2 text-xs">
                <span className="flex h-5 w-5 items-center justify-center rounded-[0.25rem] border border-border bg-card text-[0.625rem] font-semibold">
                  {edge.n}
                </span>
                <span className="text-ink-soft">
                  <span className="font-medium text-foreground">
                    {edge.from} → {edge.to}
                  </span>{' '}
                  · {edge.txt}
                </span>
                {edge.ret && <StatusPill tone="proposed">{FLOW_CHROME.returnPill}</StatusPill>}
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{FLOW_CHROME.edgePanel.note}</p>
        </Panel>

        <Panel heading={FLOW_CHROME.scopePanel.heading}>
          <p className="text-sm text-ink-soft">{FLOW_CHROME.scopePanel.tail}</p>
          <p className="mt-2 text-xs text-muted-foreground">{FLOW_CHROME.scopePanel.note}</p>
        </Panel>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
        <SectionLabel>{FLOW_CHROME.lanesLabel}</SectionLabel>
        {lanes.map((lane) => (
          <span key={lane.key} className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{lane.label}</span> · {lane.sub}
          </span>
        ))}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{FLOW_CHROME.lanesNote}</p>

      <p className="mt-3 text-xs text-muted-foreground">{FLOW_CHROME.densityNote}</p>
    </div>
  )
}

function GroupRow({ label }: { label: string }) {
  return (
    <tr>
      <td colSpan={5} className="pb-1 pt-4">
        <SectionLabel>{label}</SectionLabel>
      </td>
    </tr>
  )
}

function StepRow({
  step,
  edges,
  open,
  onToggle,
}: {
  step: FlowStep
  edges: readonly FlowEdge[]
  open: boolean
  onToggle: (n: string) => void
}) {
  const activation = activationFor(step.n)
  const counts = [
    { label: FLOW_CHROME.blocks.admin, value: activation.admin.length },
    { label: FLOW_CHROME.blocks.org, value: activation.org.length },
    { label: FLOW_CHROME.blocks.client, value: activation.client.length },
    { label: FLOW_CHROME.blocks.xc, value: activation.xc.length },
  ]

  return (
    <>
      <tr
        data-testid={`flow-step-${step.n}`}
        data-gate={step.gate ? 'true' : undefined}
        className={cn(
          'border-t border-hairline align-top',
          // A gate is DECIDED furniture, so it is aubergine with a left rule —
          // deliberately NOT mint, which means "what PX proposes".
          step.gate && 'border-l-4 border-l-primary'
        )}
      >
        <td className="py-3 pl-2 pr-3">
          <span className="v-figure font-mono text-xs tabular-nums text-muted-foreground">
            {step.n.replace(FLOW_CHROME.stepPrefix, '')}
          </span>
          {step.gate && (
            <div className="mt-1">
              <StatusPill tone="decided">{FLOW_CHROME.gatePill}</StatusPill>
            </div>
          )}
        </td>
        <td className="py-3 pr-3">
          <div className="text-sm font-semibold text-foreground">{step.title}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">{step.actor}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {FLOW_CHROME.stateLabels.env} {step.state.env} · {FLOW_CHROME.stateLabels.proj}{' '}
            {step.state.proj}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {step.creates.map((created) => (
              <span
                key={created}
                className="rounded-md border border-border bg-background px-1.5 py-0.5 text-[0.625rem] text-ink-soft"
              >
                {created}
              </span>
            ))}
          </div>
        </td>
        <td className="py-3 pr-3">
          <div className="text-xs text-muted-foreground">{FLOW_CHROME.cannotProceed}</div>
          <div className="mt-0.5 text-sm text-ink-soft">{step.gateTxt}</div>
          {step.gate && (
            <div className="mt-1.5 text-xs text-muted-foreground">{FLOW_CHROME.gateOutcomes}</div>
          )}
        </td>
        <td className="py-3 pr-3">
          <div className="flex flex-wrap items-center gap-2">
            {counts.map((count) => (
              <span
                key={count.label}
                className={cn(
                  'inline-flex items-center gap-1 text-[0.6875rem]',
                  // A zero is REAL and renders as 0 in muted ink, never blank.
                  count.value === 0 ? 'text-muted-foreground' : 'text-ink-soft'
                )}
              >
                {count.label}
                <span className="v-figure font-semibold tabular-nums">{count.value}</span>
              </span>
            ))}
          </div>
          <div className="mt-1.5 flex items-center gap-1">
            <span className="mr-1 text-[0.625rem] uppercase tracking-[0.08em] text-muted-foreground">
              {FLOW_CHROME.edgesLabel}
            </span>
            {edges.map((edge) => {
              const live = activation.edges.includes(edge.n)
              const description = `${FLOW_CHROME.edgeWord} ${edge.n} ${edge.from} ${FLOW_CHROME.toWord} ${edge.to}, ${live ? FLOW_CHROME.live : FLOW_CHROME.dormant}`
              return (
                <span
                  key={edge.n}
                  title={`${edge.from} → ${edge.to} · ${edge.txt}`}
                  className={cn(
                    'flex h-5 w-5 items-center justify-center rounded-[0.25rem] border text-[0.625rem] font-semibold',
                    live
                      ? 'border-border bg-secondary text-secondary-foreground'
                      : 'border-dashed border-border text-muted-foreground'
                  )}
                >
                  {edge.n}
                  <span className="sr-only">{description}</span>
                </span>
              )
            })}
          </div>
        </td>
        <td className="py-3 pr-1">
          <button
            type="button"
            onClick={() => onToggle(step.n)}
            aria-expanded={open}
            className="inline-flex items-center gap-1 rounded-md border border-input px-2 py-1 text-[0.6875rem] text-muted-foreground hover:bg-muted"
          >
            {step.does.length} {FLOW_CHROME.actionsWord} · {step.sys.length}{' '}
            {FLOW_CHROME.behavioursWord}
            {open ? (
              <ChevronDown className="h-3 w-3" aria-hidden />
            ) : (
              <ChevronRight className="h-3 w-3" aria-hidden />
            )}
          </button>
          <div className="mt-1 text-[0.625rem] text-muted-foreground">
            {activationWidth(activation)} {FLOW_CHROME.itemsWord}
          </div>
        </td>
      </tr>
      {open && (
        <tr className="align-top">
          <td />
          <td colSpan={4} className="pb-3 pr-1">
            <div className="grid grid-cols-2 gap-4 rounded-md border border-border bg-muted px-3 py-2">
              <div>
                <SectionLabel>{FLOW_CHROME.doesHeading}</SectionLabel>
                <ul className="mt-1 list-disc pl-4 text-xs text-ink-soft">
                  {step.does.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <SectionLabel>{FLOW_CHROME.sysHeading}</SectionLabel>
                <ul className="mt-1 list-disc pl-4 text-xs text-ink-soft">
                  {step.sys.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-muted-foreground">{step.watch}</p>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}


const range = (steps: readonly FlowStep[]): string => {
  const first = steps[0]?.n.replace(FLOW_CHROME.stepPrefix, '') ?? ''
  const last = steps[steps.length - 1]?.n.replace(FLOW_CHROME.stepPrefix, '') ?? ''
  return `${first}–${last}`
}

/** The gate step's own watch-out line, verbatim — not a paraphrase. */
const gateWatch = (steps: readonly FlowStep[]): string =>
  steps.find((s) => s.gate)?.watch ?? ''
